import { Inject, Injectable, OnDestroy, Optional } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  CONSTANTS,
  ChatAvailabilityEnum,
  ChatChannelTypeEnum,
  ChatSocketService,
  ChatUnreadUpdatedData,
  ChatUserDisplayMap,
  Constants,
} from 'lib-trend-core';
import { Subject, interval } from 'rxjs';
import { distinctUntilChanged, takeUntil, withLatestFrom } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { ChatNotificationGateService } from '../chat-preferences/chat-notification-gate.service';
import * as ChatActions from './chat.actions';
import { ChatShellService } from '../services/chat-shell.service';
import { activeChannelIdSelector, statusesSelector, userDisplaySelector } from './chat.selectors';

/** How long the incoming-message toast stays on screen. */
const CHAT_TOAST_TIMER_MS = 7000;

/** How often expired timed statuses are swept out of the store (AC-25). */
const STATUS_EXPIRY_SWEEP_MS = 30_000;

/**
 * Internal Chat socket → store bridge (SPEC-2026-07-internal-chat, T-18).
 *
 * Single subscriber of the ChatSocketService fan-out streams that translate
 * into store actions: unread/preview updates, channel lifecycle, presence,
 * status and pin updates. Joins the personal + presence rooms and (re)loads
 * the bootstrap snapshot on init and on every reconnection.
 *
 * NOT bridged here (by design — production-incident hard rule): messages of
 * the OPEN conversation. ChatConversationComponent consumes `messageNew$` /
 * `messageUpdated$` / `messageDeleted$` / `reactionUpdated$` / `typing$`
 * directly into its component-local array.
 *
 * Provided by the EAGER ChatStateModule (imported via ChatBubbleModule →
 * LayoutModule), so it lives in the root injector and is shared by the bubble
 * popup and the lazy `/chat` full view. `init()` is idempotent — the first
 * chat surface that mounts activates it.
 */
@Injectable()
export class ChatSocketBridgeService implements OnDestroy {

  private readonly destroy$ = new Subject<void>();

  private initialized = false;

  /** Conversation currently open in a chat surface — never alerted (T-21). */
  private activeChannelId: string | null = null;

  /** Live display map — names the toast without re-querying the store. */
  private userDisplay: ChatUserDisplayMap = {};

  constructor(
    private readonly store: Store,
    private readonly chatSocketService: ChatSocketService,
    private readonly notificationGate: ChatNotificationGateService,
    private readonly translateService: TranslateService,
    private readonly chatShell: ChatShellService,
    @Optional() @Inject(CONSTANTS) private readonly constants: Constants | null,
  ) { }

  /** Idempotent activation — joins rooms, wires streams, loads the snapshot. */
  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.chatSocketService.joinPersonal();
    this.chatSocketService.joinPresence();

    this.store.select(activeChannelIdSelector)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((channelId) => this.activeChannelId = channelId);

    this.store.select(userDisplaySelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((userDisplay) => this.userDisplay = userDisplay ?? {});

    this.chatSocketService.unreadUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((update) => {
        this.store.dispatch(ChatActions.applyUnreadUpdate({ update }));
        // Counters always update; the audible alert is gated (T-21 · RF-28/RF-36).
        this.maybeAlert(update);
      });

    // Deleted last message (RF-10): the fan-out carries the persisted
    // `[deleted]` token only in that case — the list row stops previewing
    // content that no longer exists, without refetching. The open pane keeps
    // consuming `messageDeleted$` directly (hard rule above).
    this.chatSocketService.messageDeleted$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (!data?.channelId || data.preview === undefined) return;
        this.store.dispatch(ChatActions.setConversationPreview({
          channelId: data.channelId,
          preview: data.preview,
        }));
      });

    this.chatSocketService.channelUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((update) => {
        const currentUserId = this.getCurrentUserId();
        this.store.dispatch(ChatActions.applyChannelUpdate({ update, currentUserId }));
        // Auto-open a channel/group I just created from the create dialogs —
        // the CHANNEL_CREATE ack carries no channelId, so the `created`
        // fan-out is the only id source. DIRECT is excluded: DM drafts are
        // promoted by the reducer via directKey on the next bootstrap.
        if (
          update?.action === 'created'
          && update.actorId === currentUserId
          && update.type !== ChatChannelTypeEnum.DIRECT
        ) {
          this.store.dispatch(ChatActions.openConversation({ channelId: update.channelId }));
        }
      });

    this.chatSocketService.presenceUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((update) => this.store.dispatch(ChatActions.applyPresenceUpdate({ update })));

    // DIRECT read watermark of the counterpart (RF-33/AC-29).
    this.chatSocketService.readUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((update) => this.store.dispatch(ChatActions.applyReadUpdate({
        update,
        currentUserId: this.getCurrentUserId(),
      })));

    this.chatSocketService.statusUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((update) => this.store.dispatch(ChatActions.applyStatusUpdate({ update })));

    // Cross-surface state (SPEC-2026-09-internal-chat-pwa, RF-23): the same user acting
    // on another connection — the chat app, another tab, the phone. The acting
    // connection already applied the change; applying it again here is idempotent.
    this.chatSocketService.favoriteUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((update) => {
        if (!Array.isArray(update?.favoriteSections)) return;
        this.store.dispatch(ChatActions.setFavoriteSections({ favoriteSections: update.favoriteSections }));
      });

    this.chatSocketService.savedUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((update) => {
        if (!update?.messageId) return;
        this.store.dispatch(ChatActions.applySavedToggle({ messageId: update.messageId, saved: !!update.saved }));
      });

    this.chatSocketService.settingsUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((update) => {
        if (update?.notificationLevel?.channelId && update.notificationLevel.level) {
          this.store.dispatch(ChatActions.setNotificationLevel({
            channelId: update.notificationLevel.channelId,
            level: update.notificationLevel.level,
          }));
        }
        if (update?.preferences) {
          this.store.dispatch(ChatActions.patchMySettings({
            patch: {
              quietHoursEnabled: !!update.preferences.quietHoursEnabled,
              quietHoursStart: update.preferences.quietHoursStart ?? undefined,
              quietHoursEnd: update.preferences.quietHoursEnd ?? undefined,
              soundEnabled: update.preferences.soundEnabled !== false,
            },
          }));
        }
      });

    // Alert priority (RF-21): the gateway tells every connection of mine whether a
    // chat app session is up — a snapshot right after the personal room is joined
    // (so a reconnect refreshes it) and every transition after that.
    this.chatSocketService.surfaceUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((signal) => this.notificationGate.setChatAppConnected(!!signal?.webChatConnected));

    // AC-25 — timed custom statuses clear THEMSELVES while the app is open.
    // The backend only clears lazily on the next read (bootstrap), and store
    // entries otherwise keep an elapsed `statusExpiresAt` forever — so every
    // surface without its own expiry clock (status button, identity card,
    // sidebar dots, tooltips) kept showing the stale status. Sweeping the
    // STORE with the same action the socket fan-out uses refreshes every
    // consumer at once; a swept entry loses its expiry, so it is dispatched
    // exactly once.
    interval(STATUS_EXPIRY_SWEEP_MS)
      .pipe(
        withLatestFrom(this.store.select(statusesSelector)),
        takeUntil(this.destroy$),
      )
      .subscribe(([, statuses]) => {
        const now = Date.now();
        Object.entries(statuses ?? {}).forEach(([userId, entry]) => {
          if (!entry?.statusExpiresAt) return;
          const expiresAt = new Date(entry.statusExpiresAt).getTime();
          if (Number.isNaN(expiresAt) || expiresAt > now) return;
          // Same reversal the render util applies (chat-status.util):
          // availability included — the whole entry expires.
          this.store.dispatch(ChatActions.applyStatusUpdate({
            update: {
              userId,
              availability: ChatAvailabilityEnum.ACTIVE,
              statusEmoji: null,
              statusText: null,
              statusExpiresAt: null,
            },
          }));
        });
      });

    this.chatSocketService.pinUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((update) => this.store.dispatch(ChatActions.applyPinUpdate({ update })));

    // Counters/previews may have drifted while offline — re-bootstrap on every
    // reconnection (queued commands were already flushed by ChatSocketService).
    this.chatSocketService.onReconnect$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.store.dispatch(ChatActions.loadBootstrap()));

    this.store.dispatch(ChatActions.loadBootstrap());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * In-app alert for an incoming message (T-21 · RF-28/RF-36 · AC-24/AC-32):
   * toast + sound. Only the NEW-MESSAGE shape of `chat:unread:updated` qualifies
   * (`messageId` present — the READ/MARK_UNREAD reset shape carries none), never
   * my own message and never the conversation already on screen. Everything
   * else is decided by ChatNotificationGateService: per-conversation level,
   * quiet hours and the sound preference. The unread counter itself is never
   * gated.
   *
   * The toast is the ONLINE counterpart of the FCM foreground toast raised by
   * ChatPushService: the backend only pushes to recipients with no active
   * presence, so without this an online user would get a badge and a sound but
   * never the visual alert AC-24 describes. Both paths share `canNotify()` so
   * they can never disagree.
   */
  private maybeAlert(update: ChatUnreadUpdatedData): void {
    if (!update?.channelId || !update.messageId || (update.count ?? 0) <= 0) return;
    if (update.senderId && update.senderId === this.getCurrentUserId()) return;
    if (update.channelId === this.activeChannelId) return;

    const mentioned = !!update.mentioned;
    // Sound carries the extra sound-preference gate; the toast only needs canNotify.
    this.notificationGate.playAlert(update.channelId, { mentioned });

    if (!this.notificationGate.canNotify(update.channelId, { mentioned })) return;
    this.showIncomingToast(update);
  }

  /** Top-end toast naming the sender, with an action that opens the conversation. */
  private showIncomingToast(update: ChatUnreadUpdatedData): void {
    const senderName = update.senderId ? this.userDisplay[update.senderId]?.name : null;
    const title = update.mentioned
      ? this.translateService.instant('chat.notify.mentionTitle', { name: senderName ?? '' }).trim()
      : senderName ?? this.translateService.instant('chat.notify.newMessageTitle');

    Swal.fire({
      title,
      text: update.preview ?? '',
      icon: 'info',
      position: 'top-end',
      backdrop: false,
      toast: true,
      timer: CHAT_TOAST_TIMER_MS,
      timerProgressBar: true,
      showConfirmButton: true,
      confirmButtonText: this.translateService.instant('chat.notify.open'),
    }).then((result) => {
      if (!result.isConfirmed) return;
      this.store.dispatch(ChatActions.openConversation({ channelId: update.channelId }));
      this.chatShell.open({ channelId: update.channelId });
    });
  }

  /** Same storage AbstractComponent.getCurrentUserUser() reads. */
  private getCurrentUserId(): string {
    try {
      const storageKey = this.constants?.['currentUser'];
      if (!storageKey) return '';
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored)?.user?._id ?? '' : '';
    } catch {
      return '';
    }
  }
}
